import React, { ChangeEvent, useEffect } from 'react';

import dayjs from 'dayjs';
import clsx from 'clsx';
import { useFormContext } from 'react-hook-form';

import DatePickerInput from 'components/DatePicker';
import { DATE_PICKER_INPUT_DEFAULT_VALUE } from 'components/DatePicker/datePickerInput.constants';
import Input from 'components/Inputs/Input';
import { DateFormat } from 'enums/DateFormat';
import { MIN_YEAR } from 'utils/constants';
import { getMessageForRequiredFields } from 'utils/validationUtils';

import {
  FIELD_MAX_LENGTH,
  INVALID_DATE_ERROR_MESSAGE,
  MAX_LENGTH_ERROR_MESSAGE,
  EMPLOYMENT_POSITION_FIELD_MAX_LENGTH,
  EMPLOYMENT_POSITION_MAX_LENGTH_ERROR_MESSAGE,
} from '../employmentInformationBlock.constants';
import { EmploymentFieldNames, EmploymentFieldsSettings } from '../employmentInformationBlock.types';
import styles from './EmploymentFormRow.module.scss';
import { setFirstDayOfMonth } from './employmentFormRow.utils';
import { PreviousEmploymentFieldNames, PrimaryEmploymentFieldNames } from '../employmentInformationBlock.enums';

interface EmploymentFormRowProps {
  title: string;
  fieldNames: EmploymentFieldNames;
  fieldsSettings: EmploymentFieldsSettings;
  maxHireDate?: Date;
}

const EmploymentFormRow = ({ title, fieldNames, maxHireDate, fieldsSettings }: EmploymentFormRowProps) => {
  const {
    trigger,
    setValue,
    watch,
    control,
    register,
    unregister,
    formState: { errors, isSubmitted },
  } = useFormContext();
  const watcher = watch();
  const currentDate = new Date();
  const minDate = new Date(MIN_YEAR, 0, 1);
  const maxDate = maxHireDate ? setFirstDayOfMonth(maxHireDate) : setFirstDayOfMonth(currentDate);

  const validateDate = (date: string) => {
    const inputDate = dayjs(date, DateFormat.MonthYear);

    if (!inputDate.isValid() || inputDate.isAfter(dayjs())) {
      return INVALID_DATE_ERROR_MESSAGE;
    }

    if (fieldNames.HireDate === PreviousEmploymentFieldNames.HireDate) {
      const primaryHireDateString = watch(PrimaryEmploymentFieldNames.HireDate);
      const primaryHireDate = dayjs(primaryHireDateString, DateFormat.MonthYear);

      if (inputDate.isSameOrAfter(primaryHireDate)) {
        return INVALID_DATE_ERROR_MESSAGE;
      }
    }

    return true;
  };

  const onChangeDate = (event: ChangeEvent<HTMLInputElement>, name: string) => {
    const date = event.target.value;

    setValue(name, date === DATE_PICKER_INPUT_DEFAULT_VALUE ? '' : date);

    if (isSubmitted) {
      trigger(name);
    }
  };

  useEffect(() => {
    if (fieldsSettings.BusinessName.isVisible) {
      register(fieldNames.BusinessName, {
        required: getMessageForRequiredFields(fieldsSettings.BusinessName.label),
        validate: (value) => {
          if (value.length > FIELD_MAX_LENGTH) {
            return MAX_LENGTH_ERROR_MESSAGE;
          }
        },
      });
    }

    if (fieldsSettings.Position.isVisible) {
      register(fieldNames.Position, {
        required: getMessageForRequiredFields(fieldsSettings.Position.label),
        validate: (value) => {
          if (value.length > EMPLOYMENT_POSITION_FIELD_MAX_LENGTH) {
            return EMPLOYMENT_POSITION_MAX_LENGTH_ERROR_MESSAGE;
          }
        },
      });
    }

    if (fieldsSettings.HireDate.isVisible) {
      register(fieldNames.HireDate, {
        required: getMessageForRequiredFields(fieldsSettings.HireDate.label),
      });
    }

    return () => {
      unregister([fieldNames.HireDate, fieldNames.BusinessName, fieldNames.Position]);
    };
  }, [
    fieldNames.BusinessName,
    fieldNames.HireDate,
    fieldNames.Position,
    fieldsSettings.BusinessName.isVisible,
    fieldsSettings.BusinessName.label,
    fieldsSettings.HireDate.isVisible,
    fieldsSettings.HireDate.label,
    fieldsSettings.Position.isVisible,
    fieldsSettings.Position.label,
    register,
    unregister,
  ]);

  return (
    <div className={styles.formRow}>
      <div className={styles.title}>{title}</div>
      <div className={styles.inputsRow}>
        {fieldsSettings.BusinessName.isVisible && (
          <Input
            className={styles.inputContainer}
            name={fieldNames.BusinessName}
            disabled={fieldsSettings.BusinessName.isDisabled}
            value={watcher[fieldNames.BusinessName]}
            label={fieldsSettings.BusinessName.label}
            inputContainerClassName={clsx(styles.input, styles.professionGovernment)}
            errorMessage={errors[fieldNames.BusinessName]?.message}
            onChange={(event) => {
              setValue(fieldNames.BusinessName, event.target.value);
              trigger(fieldNames.BusinessName);
            }}
          />
        )}
        {fieldsSettings.Position.isVisible && (
          <Input
            className={styles.inputContainer}
            name={fieldNames.Position}
            disabled={fieldsSettings.Position.isDisabled}
            value={watcher[fieldNames.Position]}
            label={fieldsSettings.Position.label}
            inputContainerClassName={clsx(styles.input, styles.professionGovernment)}
            errorMessage={errors[fieldNames.Position]?.message}
            onChange={(event) => {
              setValue(fieldNames.Position, event.target.value);
              trigger(fieldNames.Position);
            }}
          />
        )}
        {fieldsSettings.HireDate.isVisible && (
          <DatePickerInput
            className={styles.inputDateContainer}
            name={fieldNames.HireDate}
            disabled={fieldsSettings.HireDate.isDisabled}
            mask="99/9999"
            format={DateFormat.MonthYear}
            control={control}
            label={fieldsSettings.HireDate.label}
            errorMessage={errors[fieldNames.HireDate]?.message}
            value={watcher[fieldNames.HireDate]}
            minDate={minDate}
            maxDate={maxDate}
            required
            onChange={(event) => onChangeDate(event, fieldNames.HireDate)}
            validate={validateDate}
          />
        )}
      </div>
    </div>
  );
};

export default EmploymentFormRow;
